/**
 * Kriegsakademie overlay: the instructor's notes — a typed card clipped to the map table that
 * walks through the lessons in tutorial/steps.ts — and a pulsing brass ring around the HUD
 * element each lesson talks about (elements opt in with `data-tut="<anchor>"`).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { bus } from '../../game/bus';
import { useGame } from '../../game/store';
import { useT } from '../../i18n';
import { tutorialSnapshot, useTutorialDriver } from '../../tutorial/driver';
import { lessonText, TUTORIAL_STEPS } from '../../tutorial/steps';
import { tutorialStore, useTutorial } from '../../tutorial/store';
import { Button, cx } from '../common/controls';
import { Icon } from '../common/Icon';
import { Paper, Stamp } from '../common/Panel';
import { richText } from '../common/RichText';
import { useHud } from './hudStore';
import s from './instructor.module.css';

const ROMAN = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
  'XIII',
  'XIV',
  'XV',
  'XVI',
];
const roman = (n: number) => ROMAN[n - 1] ?? String(n);

/** How long the graduation stamp stays on the table (ms). */
const DIPLOMA_MS = 2600;

/** The notes' markup: `[Key]` → keycap, `*text*` → emphasis (ui/common/RichText.tsx). */
const renderNote = (text: string) => richText(text, { key: s.key });

/**
 * Bring the lesson's place into view. The camera centres a hex two rows further south, so the
 * place itself sits in the upper half of the screen, clear of the instructor's card.
 */
function focusStep(index: number): void {
  const focus = TUTORIAL_STEPS[index]?.focus;
  const snap = tutorialSnapshot();
  if (!focus || !snap) return;
  const hex = focus(snap);
  if (hex === null || hex < 0) return;
  const { grid } = snap;
  const below = grid.id(grid.col(hex), Math.min(grid.height - 1, grid.row(hex) + 2));
  bus.emit('focusHex', { hex: below, zoom: 1.4 });
}

export function Instructor() {
  const t = useT();
  useTutorialDriver();
  const active = useTutorial((t) => t.active);
  const index = useTutorial((t) => t.step);
  const doneStep = useTutorial((t) => t.doneStep);
  const hasView = useGame((g) => g.view !== null);
  const over = useGame((g) => g.view?.winners != null);
  const [diploma, setDiploma] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const shown = diploma || (active && hasView && !over);

  // Publish the card's height (CSS px inside the HUD zoom) so the wide research drawer can end
  // above it instead of disappearing under the notes.
  useLayoutEffect(() => {
    const el = cardRef.current;
    const root = document.documentElement.style;
    if (!shown || !el) return;
    const publish = () => root.setProperty('--tut-card-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.removeProperty('--tut-card-h');
    };
  }, [shown]);

  // Each lesson that concerns a place on the map brings the camera there.
  useEffect(() => {
    if (!active || !hasView) return;
    const t = window.setTimeout(() => focusStep(index), 350);
    return () => clearTimeout(t);
  }, [active, index, hasView]);

  useEffect(() => {
    if (!diploma) return;
    const t = window.setTimeout(() => setDiploma(false), DIPLOMA_MS);
    return () => clearTimeout(t);
  }, [diploma]);

  if (diploma)
    return (
      <div ref={cardRef} className={s.card}>
        <Paper className={cx(s.sheet, s.diploma)}>
          <div className={s.head}>
            <span>{t('ka.cert.head')}</span>
          </div>
          <div className={s.title}>{t('ka.cert.title')}</div>
          <p className={s.p}>{t('ka.cert.text')}</p>
          <Stamp className={s.bigStamp} tone="green" rotate={-10} animate delay={120}>
            {t('ka.cert.stamp')}
          </Stamp>
        </Paper>
      </div>
    );
  if (!active || !hasView || over) return null;

  const step = TUTORIAL_STEPS[index];
  const text = lessonText(step, t.locale);
  const total = TUTORIAL_STEPS.length;
  const last = index === total - 1;
  const reading = !step.done;
  const done = doneStep === index;
  const academy = tutorialStore.getState();

  const advance = () => {
    if (last) {
      bus.emit('sound', { name: 'stamp' });
      setDiploma(true);
    } else bus.emit('sound', { name: 'paper' });
    academy.next();
  };

  return (
    <div ref={cardRef} className={s.card}>
      <Paper className={s.sheet}>
        <span className={s.clip} aria-hidden />
        <div className={s.head}>
          <span>{t('ka.head')}</span>
          <span className={s.lesson}>{t('ka.lesson', { n: roman(index + 1), total: roman(total) })}</span>
          <button
            type="button"
            className={s.end}
            title={t('ka.end')}
            onClick={() => {
              bus.emit('sound', { name: 'paper' });
              academy.finish(false);
            }}
          >
            <Icon name="close" size={13} />
          </button>
        </div>
        <div key={index} className={s.page}>
          <div className={s.title}>{text.title}</div>
          {text.body.map((p) => (
            <p key={p} className={s.p}>
              {renderNote(p)}
            </p>
          ))}
          {text.task && (
            <div className={cx(s.task, done && s.taskDone)}>
              <span className={s.box}>{done && <Icon name="check" size={13} />}</span>
              {renderNote(text.task)}
            </div>
          )}
        </div>
        <div className={s.foot}>
          <Button size="sm" variant="ink" icon="chevronLeft" disabled={index === 0} onClick={academy.back}>
            {t('ka.back')}
          </Button>
          {step.focus && (
            <Button size="sm" variant="ink" icon="focus" onClick={() => focusStep(index)}>
              {t('ka.showMe')}
            </Button>
          )}
          <span className={s.pips} aria-hidden>
            {TUTORIAL_STEPS.map((st, i) => (
              <i key={st.id} className={cx(i < index && s.pipDone, i === index && s.pipNow)} />
            ))}
          </span>
          <Button
            size="sm"
            variant={reading || done ? 'primary' : 'secondary'}
            icon={last ? 'star' : undefined}
            silent
            onClick={advance}
            title={reading || done ? undefined : t('ka.skip.hint')}
          >
            {last ? t('ka.graduate') : reading || done ? t('ka.next') : t('ka.skip')}
          </Button>
        </div>
        {done && (
          <Stamp className={s.stamp} tone="green" rotate={-8} animate delay={0}>
            {t('ka.done')}
          </Stamp>
        )}
      </Paper>
    </div>
  );
}

/** Pulsing ring around the HUD element the current lesson is about (drawn outside the HUD zoom). */
export function TutorialHighlight() {
  const active = useTutorial((t) => t.active);
  const index = useTutorial((t) => t.step);
  const overlay = useHud((h) => h.overlay);
  const over = useGame((g) => g.view?.winners != null);
  const anchor = active && !overlay && !over ? TUTORIAL_STEPS[index]?.highlight : undefined;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchor) return;
    let raf = 0;
    let last = '';
    const loop = () => {
      const ring = ref.current;
      const r = document.querySelector(`[data-tut="${anchor}"]`)?.getBoundingClientRect();
      const key = r && r.width > 0 && r.height > 0 ? `${r.x}|${r.y}|${r.width}|${r.height}` : '';
      if (ring && key !== last) {
        last = key;
        ring.style.opacity = key ? '1' : '0';
        if (r && key) {
          const pad = 5;
          ring.style.left = `${r.x - pad}px`;
          ring.style.top = `${r.y - pad}px`;
          ring.style.width = `${r.width + pad * 2}px`;
          ring.style.height = `${r.height + pad * 2}px`;
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [anchor]);

  if (!anchor) return null;
  return <div ref={ref} className={s.ring} style={{ opacity: 0 }} aria-hidden />;
}
